import { memo, useCallback } from 'react';
import { useSet } from 'react-use';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import { MedicinePrescriptionQuery } from '@dreryk/egabinet-prescription-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';
import {
   commonTranslationKeys,
   createInterpolatedTranslationKey,
   createTranslatedText,
   TranslatableText,
} from '@dreryk/react-common';
import {
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   Message,
   MessageType,
   ModalCancelButton,
   ModalConfirmButton,
   StandardTabs,
} from '@dreryk/react-components';

import { OnPatientEntitlementDocumentCreated, PatientEntitlementCreateForm } from '~legacy.patient';

import { draftPrescriptionsTranslationKeys } from '../../../../../translations';
import { PatientId } from '../../../PrescriptionIssuingPanel.types';

import { StyledPatientEntitlementsDocumentTabsContent } from './PatientEntitlementsDocumentTabs.styled';

const PATIENT_ENTITLEMENT_DOCUMENT_TYPES: EntitlementType[] = ['C', 'ZK', 'WE', 'IB', 'IW'];

type PatientEntitlementsDocumentTabsProps = {
   readonly entitlementsRequiredDocumentMissing: EntitlementType[];
   readonly patientId: PatientId;
   readonly onAllEntitlementsRequiredDocumentMissingProvided: VoidFunction;
   readonly onCancel: VoidFunction;
   readonly confirmationButtonLabel: TranslatableText | undefined;
};

// eslint-disable-next-line max-lines-per-function
export const PatientEntitlementsDocumentTabs = memo(
   // eslint-disable-next-line max-lines-per-function
   (props: PatientEntitlementsDocumentTabsProps): JSX.Element => {
      const [addedPatientEntitlementDocumentsSet, { add: addPatientEntitlementDocument }] = useSet<EntitlementType>();

      const handlePatientEntitlementDocumentCreated: OnPatientEntitlementDocumentCreated = useCallback(
         (entitlementType: EntitlementType) => {
            addPatientEntitlementDocument(entitlementType);
            dispatchSnackbarPositiveEvent(draftPrescriptionsTranslationKeys.additionalEntitlementDocumentsDataWasSaved);
         },
         [addPatientEntitlementDocument]
      );

      const handleCancel = useCallback(() => {
         props.onCancel();

         if (addedPatientEntitlementDocumentsSet.size) {
            refetchCqrsQuery(MedicinePrescriptionQuery);
         }

         if (addedPatientEntitlementDocumentsSet.size !== props.entitlementsRequiredDocumentMissing.length) {
            const entitlementsRequiredDocumentMissing = props.entitlementsRequiredDocumentMissing.filter(
               (entitlementType) => !addedPatientEntitlementDocumentsSet.has(entitlementType)
            );
            if (entitlementsRequiredDocumentMissing.length > 1) {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(
                     draftPrescriptionsTranslationKeys.prescriptioNCantBeSentWithoutDocumentConfirmingEntitlements,
                     {
                        entitlementTypes: entitlementsRequiredDocumentMissing.join(', '),
                     }
                  )
               );
            } else {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(
                     draftPrescriptionsTranslationKeys.prescriptionCantBeSentWithoutDocumentConfirmingEntitlement,
                     {
                        entitlementType: entitlementsRequiredDocumentMissing.at(0),
                     }
                  )
               );
            }
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [props.onCancel, props.entitlementsRequiredDocumentMissing, addedPatientEntitlementDocumentsSet]);

      const handleTabChange = useCallback(
         (tabId: string | null): void => {
            if (!tabId) {
               refetchCqrsQuery(MedicinePrescriptionQuery);
               props.onAllEntitlementsRequiredDocumentMissingProvided();
            }
         },
         // eslint-disable-next-line react-hooks/exhaustive-deps
         [props.onAllEntitlementsRequiredDocumentMissingProvided]
      );

      return (
         <StyledPatientEntitlementsDocumentTabsContent direction={'column'} gap={'8px'}>
            <Message
               type={MessageType.WARNING}
               fullWidth
               displayIndicator
               label={draftPrescriptionsTranslationKeys.informationFromEligibilityDocumentsRequiredToUseRefund}
               testId={'patientEntitlementsDocuments'}
            />
            <StandardTabs testId={'patientEntitlementsDocuments'} onTabChange={handleTabChange}>
               {PATIENT_ENTITLEMENT_DOCUMENT_TYPES.filter((entitlementType: EntitlementType) =>
                  props.entitlementsRequiredDocumentMissing.includes(entitlementType)
               ).map((entitlementType: EntitlementType) => {
                  return (
                     <StandardTabs.Tab
                        key={entitlementType}
                        id={entitlementType}
                        testId={entitlementType}
                        label={draftPrescriptionsTranslationKeys.entitlement}
                        slot={createTranslatedText(entitlementType)}
                        disabled={addedPatientEntitlementDocumentsSet.has(entitlementType)}
                     >
                        <PatientEntitlementCreateForm
                           entitlementType={entitlementType}
                           patientId={props.patientId}
                           onPatientEntitlementsDocumentCreated={handlePatientEntitlementDocumentCreated}
                        >
                           {({ patientEntitlementCreate }: any) => (
                              <>
                                 <ModalConfirmButton
                                    label={props.confirmationButtonLabel ?? commonTranslationKeys.save}
                                    onClick={patientEntitlementCreate}
                                 />
                                 <ModalCancelButton label={commonTranslationKeys.cancel} onClick={handleCancel} />
                              </>
                           )}
                        </PatientEntitlementCreateForm>
                     </StandardTabs.Tab>
                  );
               })}
            </StandardTabs>
         </StyledPatientEntitlementsDocumentTabsContent>
      );
   }
);
